import { memo, useCallback, useState } from 'react';

import { PopoverHoverWrapper } from '@/components/PopoverHoverWrapper';
import type { MenuItem } from '@/consts/navigation';
import { useNavigationContext } from '@/features/navigation/context/NavigationContext';
import { useActiveMenuItem } from '@/features/navigation/hooks/useActiveMenuItem';
import { useAutoExpand } from '@/features/navigation/hooks/useAutoExpand';
import { cn } from '@/utils/cn';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { NavExpandedContent } from './NavExpandedContent';
import { NavHoverPopover } from './NavHoverPopover';
import { NavMenuIcon } from './NavMenuIcon';
import { NavSubMenuList } from './NavSubMenuList';

interface NavMenuItemProps {
  item: MenuItem;
  index: number;
}

export const NavMenuItem = memo(function NavMenuItem({ item, index }: NavMenuItemProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { expandedSections, toggleSection, isNavCollapsed } = useNavigationContext();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  // Use code as unique section key when available to avoid collisions (e.g. two "Operations" items)
  const sectionKey = (item.code as string) || item.text;
  const isExpanded = expandedSections.has(sectionKey);
  const menuSlug = t(item.text).toLowerCase().replace(/\s+/g, '-');

  const { hasChildren, hasRoute, hasActiveChildren, isDirectlyActive, isUtilitySection } = useActiveMenuItem(item);
  useAutoExpand(item.isGroup ? false : hasActiveChildren, isExpanded, sectionKey);

  const handleClick = useCallback(() => {
    if (hasChildren && !isNavCollapsed) {
      toggleSection(sectionKey);
    } else if (hasRoute && !hasChildren && item.to) {
      void navigate(item.to);
    }
  }, [hasChildren, isNavCollapsed, toggleSection, sectionKey, hasRoute, item.to, navigate]);

  const handlePopoverOpenChange = useCallback((open: boolean) => {
    setIsPopoverOpen(open);
  }, []);

  // Group header — always-visible label + line, children listed directly below (no collapse)
  if (item.isGroup) {
    if (isNavCollapsed) {
      // In collapsed mode, show just a divider between groups
      return (
        <li role="none">
          {index > 0 && <div className="border-border-500/40 mx-2 my-2 border-t" />}
          {item.children && item.children.length > 0 && (
            <ul className="flex flex-col" role="menu">
              {item.children.map((child, childIndex) => (
                <NavMenuItem key={child.text} item={child} index={childIndex} />
              ))}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li role="none">
        <div className={cn('my-1.5 flex items-center gap-2 px-3', index > 0 && 'mt-2')}>
          <span className="text-nav-group text-[10px] font-semibold tracking-wider whitespace-nowrap uppercase">
            {t(item.text)}
          </span>
          <div className="bg-border-500/40 h-px flex-1" />
        </div>
        {item.children && item.children.length > 0 && (
          <ul className="flex flex-col gap-0.5" role="menu">
            {item.children.map((child, childIndex) => (
              <NavMenuItem key={child.text} item={child} index={childIndex} />
            ))}
          </ul>
        )}
      </li>
    );
  }

  const isActive = (isDirectlyActive || hasActiveChildren) && !isUtilitySection;

  const containerClassName = cn(
    'w-full flex items-center justify-between rounded-md',
    'transition-colors duration-150 cursor-pointer group relative',
    isNavCollapsed ? 'px-2 justify-center' : 'px-3',
    'py-2 text-[13px]',
    isActive ? 'bg-secondary-400/70 text-typography' : 'text-nav-item hover:bg-secondary-400/40 hover:text-typography'
  );

  const menuItemContent = (
    <div
      onClick={handleClick}
      role="menuitem"
      tabIndex={0}
      aria-expanded={hasChildren && isExpanded}
      aria-controls={`section-${index.toString()}`}
      data-testid={`menu-item-${menuSlug}`}
      title={isNavCollapsed ? t(item.text) : undefined}
      className={containerClassName}
    >
      {isNavCollapsed ? <NavMenuIcon item={item} /> : <NavExpandedContent item={item} translatedText={t(item.text)} />}
    </div>
  );

  return (
    <li key={item.text} role="none" data-testid={`menu-section-${menuSlug}`}>
      <div className="flex flex-col transition-all duration-100">
        {isNavCollapsed && hasChildren ? (
          <PopoverHoverWrapper isOpen={isPopoverOpen} onOpenChange={handlePopoverOpenChange} delay={40}>
            <NavHoverPopover item={item} isOpen={isPopoverOpen} onOpenChange={handlePopoverOpenChange}>
              {menuItemContent}
            </NavHoverPopover>
          </PopoverHoverWrapper>
        ) : (
          menuItemContent
        )}

        <NavSubMenuList item={item} index={index} />
      </div>
    </li>
  );
});
